import liff from "@line/liff";
import { env } from "./env.ts";

// Sole boundary for the LIFF SDK. Every other module in this app talks to LIFF
// through the functions exported here so tests can mock the SDK by replacing
// this module rather than replacing `@line/liff` globally.

let initPromise: Promise<void> | null = null;

export async function ensureLiffReady(): Promise<void> {
	if (!env.liffId) {
		throw new Error("VITE_LIFF_ID is not set — cast LIFF app cannot initialize");
	}
	if (!initPromise) {
		initPromise = liff.init({ liffId: env.liffId }).catch((err) => {
			// Reset so the next call gets a fresh attempt (e.g. if the WebView
			// transiently lost network during first paint).
			initPromise = null;
			throw err;
		});
	}
	await initPromise;
}

// Returns the LIFF-issued ID Token if the caller is inside LIFF and logged in
// to LINE. Null when opened outside LIFF (dev in a regular browser without a
// LINE session). The API's LINE verify endpoint requires this string.
export async function getLineIdToken(): Promise<string | null> {
	await ensureLiffReady();
	if (!liff.isLoggedIn()) {
		return null;
	}
	return liff.getIDToken();
}

// Sends the caller to LINE Login and returns to this app. `redirectUri` should
// be an absolute URL — LIFF appends its own query params so the caller sees
// itself again after the round-trip. Prefer to call this only from routes that
// explicitly need a logged-in state.
export function loginWithLine(redirectUri: string): void {
	liff.login({ redirectUri });
}

// Only used from the header dropdown / debug menu. Regular sign-out flow for a
// cast is "close the LIFF WebView" — LINE session persists across app opens.
export function logoutFromLine(): void {
	if (liff.isLoggedIn()) {
		liff.logout();
	}
}

export function isInLineClient(): boolean {
	return liff.isInClient();
}
